import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Upload } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { Loading, Block, eur, inputCls, btnGold, btnGhost } from './ui';
import { useConfirm } from '../ConfirmDialog';

/*
  Importación del CDM desde el Excel de Google Sheets.
  - Anonimizar es una herramienta del entorno de pruebas: allí se aplica siempre (lo decide el backend)
    y fuera de pruebas no se muestra ni se aplica.
  - Actualizar desde el Sheet (16/10/2026): sustituye lo que vino del Sheet y conserva lo creado en la
    plataforma. Primero simula y enseña qué cambia; exige escribir ACTUALIZAR.
  - Reemplazar un CDM ya importado borra los datos del CDM: exige escribir REEMPLAZAR.
*/
const COL_LABEL = {
  cdm_members: 'Socios', cdm_brokers: 'Brokers', cdm_payments: 'Cobros', cdm_fhct: 'Aportaciones FHCT', cdm_expenses: 'Gastos',
  cdm_investors: 'Inversores', cdm_reservations: 'Reservas', cdm_commission_marks: 'Marcas de pago', cdm_brick_adjustments: 'Ajustes de bricks',
  cdm_operations: 'Operaciones', cdm_positions: 'Posiciones',
};
const ENTITY_LABEL = { member: 'Socio', broker: 'Broker', fhct: 'FHCT', expense: 'Gasto', operation_cdm: 'Operación', position: 'Posición' };

export function CdmImport({ imported, onDone, cdm }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  // Con un CDM ya importado lo normal es actualizar desde el Sheet; reemplazar todo es la opción excepcional
  const [mode, setMode] = useState('update');
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [dialog, ask] = useConfirm();
  const staging = !!(cdm?.staging || cdm?.force_anonymize);

  const update = async () => {
    setConflicts(null);
    let sim;
    try {
      sim = await dashboardApi.cdmImport(file, { update: true, dryRun: true });
    } catch (e) {
      if (e.detail?.conflicts) setConflicts(e.detail.conflicts);
      throw e;
    }
    const changes = Object.entries(sim.counts || {})
      .filter(([, c]) => c.sheet_antes !== c.sheet_ahora || c.plataforma > 0)
      .map(([k, c]) => ({ label: COL_LABEL[k] || k, value: `${c.sheet_antes} → ${c.sheet_ahora} del Sheet${c.plataforma ? ` · ${c.plataforma} de la plataforma (se conservan)` : ''}` }));
    const over = sim.overwritten || [];
    const ok = await ask({
      title: 'Actualizar el CDM desde el Sheet',
      description: `Se sustituye lo que vino del Sheet por el contenido de «${file.name}». Lo creado en la plataforma (altas y cobros de Stripe, registros del panel, marcas de pago, ajustes de bricks) se conserva, igual que los enlaces con las cuentas.`,
      details: [
        ...changes,
        over.length
          ? `Atención: ${over.length} cambio(s) hechos en el panel sobre datos del Sheet se pisarán: ${over.slice(0, 8).map((o) => `${ENTITY_LABEL[o.entity] || o.entity} ${o.ref}`).join(', ')}${over.length > 8 ? '…' : ''}. Haz esos cambios también en el Sheet antes de actualizar, o vuelve a hacerlos aquí después.`
          : 'No hay cambios hechos en el panel sobre datos del Sheet desde la última importación.',
        staging ? 'Entorno de pruebas: nombres y emails se anonimizan.' : null,
      ],
      requireText: 'ACTUALIZAR',
      confirmLabel: 'Actualizar el CDM',
      tone: over.length ? 'danger' : 'neutral',
    });
    if (!ok) return;
    const r = await dashboardApi.cdmImport(file, { update: true, confirm: 'ACTUALIZAR' });
    toast.success(`CDM actualizado: ${r.corrections} correcciones aplicadas, ${r.pending} asuntos pendientes`);
    onDone();
  };

  const replaceAll = async () => {
    let confirm = '';
    if (imported) {
      const counts = cdm?.counts || {};
      const total = Object.values(counts).reduce((a, n) => a + (Number(n) || 0), 0);
      const ok = await ask({
        title: 'Reemplazar el CDM',
        description: `Se sustituye todo el CDM de la plataforma por el contenido de «${file.name}».`,
        details: [
          `Se borran ${total.toLocaleString('es-ES')} registros actuales del CDM (socios, brokers, cobros, aportaciones, gastos, marcas de pago…) y se cargan los del Excel.`,
          'Las cuentas de usuario de la plataforma no se tocan. El historial de cambios y los ajustes se conservan.',
          'Si hay registros creados en la plataforma después de la última importación, el reemplazo se bloquea para no perderlos.',
          staging ? 'Entorno de pruebas: nombres y emails se anonimizan.' : null,
        ],
        requireText: 'REEMPLAZAR',
        confirmLabel: 'Reemplazar el CDM',
        tone: 'danger',
      });
      if (!ok) return;
      confirm = 'REEMPLAZAR';
    }
    const r = await dashboardApi.cdmImport(file, { replace: imported, confirm });
    toast.success(`CDM importado: ${r.corrections} correcciones aplicadas, ${r.pending} asuntos pendientes`);
    onDone();
  };

  const go = async () => {
    if (!file) return;
    setBusy(true);
    try {
      if (imported && mode === 'update') await update();
      else await replaceAll();
    } catch (e) { toast.error(e.message || 'No se ha podido importar el CDM'); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-4" data-testid="cdm-import">
      {/* Sin esto la confirmación nunca se veía y el botón se quedaba en «Comprobando…» (02/11/2026) */}
      {dialog}
      <div>
        <p className="text-white font-medium">{imported ? 'Volver a importar el CDM' : 'Importar el CDM de Google Sheets'}</p>
        <p className="text-sm text-gray-400 mt-1">En la hoja: Archivo › Descargar › Microsoft Excel (.xlsx). Se aplican las correcciones aprobadas el 07/10/2026 y todo queda registrado.</p>
      </div>
      {imported && (
        <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Modo de importación">
          {[
            ['update', 'Actualizar desde el Sheet', 'Sustituye lo que vino del Sheet y conserva lo creado en la plataforma. Antes te enseñamos qué cambia.'],
            ['replace', 'Reemplazar todo', 'Borra el CDM de la plataforma y lo carga entero desde el Excel. Se bloquea si hay datos creados en la plataforma.'],
          ].map(([k, t, d]) => (
            <label key={k} className={`rounded-xl border p-3 cursor-pointer ${mode === k ? 'border-[#c9a84c]/40 bg-[#c9a84c]/[0.06]' : 'border-white/10 hover:bg-white/[0.03]'}`}>
              <span className="flex items-center gap-2 text-sm text-white"><input type="radio" name="cdm-mode" checked={mode === k} onChange={() => setMode(k)} data-testid={`cdm-import-${k}`} />{t}</span>
              <span className="block text-xs text-gray-400 mt-1">{d}</span>
            </label>
          ))}
        </div>
      )}
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files[0] || null); setConflicts(null); }} />
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => ref.current?.click()} className={btnGhost}><Upload size={14} /> {file ? file.name : 'Elegir fichero .xlsx'}</button>
        {staging && <span className="text-xs text-gray-400">Entorno de pruebas: los nombres y emails se sustituyen solos por «Socio B12…»; IDs y cifras se mantienen.</span>}
        <button disabled={!file || busy} onClick={go} className={btnGold} data-testid="cdm-import-go">{busy ? 'Comprobando…' : (imported && mode === 'update' ? 'Revisar y actualizar' : 'Importar')}</button>
      </div>
      {conflicts?.length > 0 && (
        <div className="rounded-xl border border-red-400/30 bg-red-400/[0.06] p-4 text-sm" role="alert" data-testid="cdm-import-conflicts">
          <p className="text-white font-medium">No se ha actualizado nada: {conflicts.length} registro(s) del Sheet chocan con registros creados en la plataforma</p>
          <ul className="mt-2 space-y-1 text-xs text-gray-300 list-disc pl-5">{conflicts.slice(0, 20).map((c, i) => <li key={i}>{c.text}</li>)}</ul>
          <p className="text-xs text-gray-400 mt-2">Corrígelo en el Sheet (cambia el ID o quita la fila duplicada) y vuelve a intentarlo.</p>
        </div>
      )}
    </div>
  );
}

export default function CdmAjustes({ imported, onImported, cdm }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    dashboardApi.cdmSettings().then(setS).catch((e) => { const m = e.message || 'No se han podido cargar los ajustes'; setLoadError(m); toast.error(m); });
  }, []);
  if (!s) return <Loading error={loadError} />;
  const costs = s.monthly_committed_costs || [];
  const setCost = (i, k, v) => setS({ ...s, monthly_committed_costs: costs.map((c, j) => (j === i ? { ...c, [k]: k === 'eur' ? Number(v) : v } : c)) });
  const ops = s.operations || [];
  const save = async () => {
    setBusy(true);
    try {
      const r = await dashboardApi.cdmPutSettings({
        fx_usd_eur: Number(s.fx_usd_eur), fx_market_usd_eur: Number(s.fx_market_usd_eur), fx_market_date: s.fx_market_date,
        monthly_committed_costs: costs, paid_tolerance_pct: Number(s.paid_tolerance_pct),
        operations: ops, current_operation: s.current_operation,
      });
      setS(r);
      toast.success('Ajustes guardados');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="cdm-ajustes">
      <Block title="Cambio de divisa">
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">USD → EUR de gestión</span>
            <input type="number" step="0.0001" value={s.fx_usd_eur} onChange={(e) => setS({ ...s, fx_usd_eur: e.target.value })} className={`${inputCls} w-full mt-1`} /></label>
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">USD → EUR de mercado (gastos)</span>
            <input type="number" step="0.0001" value={s.fx_market_usd_eur} onChange={(e) => setS({ ...s, fx_market_usd_eur: e.target.value })} className={`${inputCls} w-full mt-1`} /></label>
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Fecha del cambio de mercado</span>
            <input type="date" value={s.fx_market_date || ''} onChange={(e) => setS({ ...s, fx_market_date: e.target.value })} className={`${inputCls} w-full mt-1`} /></label>
        </div>
        <p className="text-xs text-gray-400">Cada apunte guarda el cambio con el que se registró: cambiarlo aquí no reescribe el pasado.</p>
      </Block>
      <Block title={`Coste recurrente comprometido · ${eur(costs.reduce((a, c) => a + (Number(c.eur) || 0), 0))}/mes`}>
        <div className="space-y-2">
          {costs.map((c, i) => (
            <div key={i} className="flex gap-2">
              <input value={c.label} onChange={(e) => setCost(i, 'label', e.target.value)} className={`${inputCls} flex-1`} aria-label="Concepto" />
              <input type="number" value={c.eur} onChange={(e) => setCost(i, 'eur', e.target.value)} className={`${inputCls} w-32`} aria-label="Euros al mes" />
              <button onClick={() => setS({ ...s, monthly_committed_costs: costs.filter((_, j) => j !== i) })} className={btnGhost} aria-label="Quitar"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setS({ ...s, monthly_committed_costs: [...costs, { label: '', eur: 0 }] })} className={btnGhost}><Plus size={14} /> Añadir coste</button>
        </div>
        <p className="text-xs text-gray-400">El runway es la caja dividida entre este coste mensual.</p>
      </Block>
      <Block title="Operaciones de FHCT">
        {ops.map((o, i) => (
          <div key={o.id} className="flex flex-wrap items-center gap-3 text-sm text-gray-300">
            <input type="radio" name="op" checked={s.current_operation === o.id} onChange={() => setS({ ...s, current_operation: o.id })} aria-label={`Operación actual ${o.id}`} />
            <span className="text-white w-12">{o.id}</span>
            <input value={o.name} onChange={(e) => setS({ ...s, operations: ops.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} className={`${inputCls} flex-1`} />
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!o.auto_upline} onChange={(e) => setS({ ...s, operations: ops.map((x, j) => (j === i ? { ...x, auto_upline: e.target.checked } : x)) })} /> Comisiones y bricks N2/N3 automáticos</label>
          </div>
        ))}
        <button onClick={() => { const id = `OP${ops.length + 1}`; setS({ ...s, operations: [...ops, { id, name: `Operación ${ops.length + 1}`, auto_upline: true }] }); }} className={btnGhost}><Plus size={14} /> Nueva operación</button>
        <p className="text-xs text-gray-400">En la primera operación N2 y N3 solo cuentan si están apuntados (decisión 07/10/2026). Las aportaciones nuevas van a la operación marcada.</p>
      </Block>
      <Block title="Comprobación del 100 % pagado">
        <label className="flex items-center gap-3 text-sm text-gray-300">Margen por diferencias de cambio
          <input type="number" step="0.005" min="0" max="0.1" value={s.paid_tolerance_pct} onChange={(e) => setS({ ...s, paid_tolerance_pct: e.target.value })} className={`${inputCls} w-24`} /> = {(Number(s.paid_tolerance_pct || 0) * 100).toLocaleString('es-ES', { maximumFractionDigits: 2 })} %
        </label>
      </Block>
      <button onClick={save} disabled={busy} className={btnGold}>Guardar ajustes</button>
      <CdmImport imported={imported} onDone={onImported} cdm={cdm} />
    </div>
  );
}
